import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Datagrid } from '@refera/ui-web'
import PropTypes from 'prop-types'

import {
  serviceOrdersSelector,
  serviceOrdersCountSelector,
  getServiceOrderLoadingSelector,
} from '_modules/service-orders/selectors'
import { userSelector } from '_/modules/authentication/selectors'
import { ROUTES, SERVICE_ORDER_VALUES } from '_utils/constants'

import { COLUMNS } from './constants'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

const ServiceOrderMainTable = ({
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
  page,
  pageSize,
  isTradesman,
}) => {
  const isServiceOrdersLoading = useSelector(getServiceOrderLoadingSelector)
  const serviceOrders = useSelector(serviceOrdersSelector)
  const serviceOrdersCount = useSelector(serviceOrdersCountSelector)
  const { checkIfUserDoesNotPermission } = useRolePermission()
  const user = useSelector(userSelector)
  const { isAdmin } = useRolePermission()
  const { useNewServiceOrder = false } = user?.params || {}

  const rows = useMemo(() => serviceOrders ?? [], [serviceOrders])

  const columns = useMemo(() => {
    if (isTradesman) return COLUMNS.TRADESMAN

    if (isAdmin) return COLUMNS.DEFAULT

    return COLUMNS.DEFAULT.filter(column => column.field !== SERVICE_ORDER_VALUES.AGENCY).map(
      column => {
        if (column.field === SERVICE_ORDER_VALUES.RESPONSIBLE) {
          return { ...column, width: 300 }
        }
        if (column.field === SERVICE_ORDER_VALUES.STEP_STATUS) {
          return { ...column, width: 400 }
        }
        return column
      }
    )
  }, [isTradesman, isAdmin])

  const handleRowClick = useCallback(
    event => {
      if (
        checkIfUserDoesNotPermission(PERMISSIONS.SERVICE_ORDER, [
          PERMISSIONS_ACTIONS.ADD,
          PERMISSIONS_ACTIONS.EDIT,
          PERMISSIONS_ACTIONS.READ,
        ])
      ) {
        return
      }
      if (isTradesman) {
        window.open(
          `${ROUTES.DETAIL_SERVICE_ORDER}/${event.row.id}`,
          '_blank',
          'noopener,noreferrer'
        )
      } else {
        const serviceOrderRoute = useNewServiceOrder
          ? ROUTES.NEW_SERVICE_ORDER
          : ROUTES.SERVICE_ORDER
        window.open(`${serviceOrderRoute}/${event.row.id}`, '_blank', 'noopener,noreferrer')
      }
    },
    [isTradesman, useNewServiceOrder]
  )

  const dataGridStyle = {
    fontSize: '1.4rem',
    '& .MuiDataGrid-cell:hover': {
      cursor: 'pointer',
    },
  }

  return (
    <Datagrid
      loading={isServiceOrdersLoading}
      rows={rows}
      columns={columns}
      paginationMode="server"
      rowCount={serviceOrdersCount}
      sx={dataGridStyle}
      onRowClick={handleRowClick}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSortModelChange={onSortModelChange}
      page={page}
      pageSize={pageSize}
    />
  )
}

ServiceOrderMainTable.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onSortModelChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  isTradesman: PropTypes.bool,
}

ServiceOrderMainTable.defaultProps = {
  isTradesman: false,
}

export default React.memo(ServiceOrderMainTable)
